import { useState } from 'react';
import { Button, Modal } from '@shared/components';
import { PermissionGuard } from '@/features/auth';

interface Props {
    settingsCount: number;
    unlinkedCount: number;
    keyName: string;
    onBulkLink: (opts?: { force?: boolean }) => void;
    isBulkLinking: boolean;
}

export function WhereUsedPanel({
    settingsCount,
    unlinkedCount,
    keyName,
    onBulkLink,
    isBulkLinking,
}: Props) {
    const [confirmForceOpen, setConfirmForceOpen] = useState(false);

    const handleConfirmForce = () => {
        onBulkLink({ force: true });
        setConfirmForceOpen(false);
    };

    const handleCancelForce = () => setConfirmForceOpen(false);

    return (
        <div className="border-t border-mp-border pt-4 space-y-2">
            <h2 className="text-lg font-semibold">Where used</h2>
            <p className="text-sm">
                {settingsCount === 0
                    ? 'This option is not currently used by any product.'
                    : `This option is used by ${settingsCount} product setting(s).`}
            </p>

            {unlinkedCount > 0 && (
                <div className="border border-mp-border rounded p-3 space-y-2 bg-mp-background">
                    <p className="text-sm">
                        <strong>{unlinkedCount}</strong> unlinked setting(s) use the key{' '}
                        <code>{keyName}</code> and could be linked to this catalog option.
                    </p>
                    <PermissionGuard permission={{ resource: 'smr', action: 'product-config-setting.bulk-link' }}>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                disabled={isBulkLinking}
                                onClick={() => onBulkLink()}
                            >
                                {isBulkLinking ? 'Linking…' : `Link ${unlinkedCount} unlinked setting(s)`}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isBulkLinking}
                                onClick={() => setConfirmForceOpen(true)}
                            >
                                Force link
                            </Button>
                        </div>
                    </PermissionGuard>
                </div>
            )}

            <Modal
                isOpen={confirmForceOpen}
                onClose={handleCancelForce}
                ariaLabelledBy="where-used-force-link-heading"
                className="max-w-md p-6"
            >
                <h2 id="where-used-force-link-heading" className="text-lg font-semibold mb-2">
                    Force link without revalidating?
                </h2>
                <p className="text-sm text-mp-muted mb-4">
                    Force-link without revalidating existing values? This links every unlinked
                    setting to this catalog option without re-checking their current values, which
                    may attach settings whose values would otherwise fail validation.
                </p>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="clear" onClick={handleCancelForce}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="danger"
                        disabled={isBulkLinking}
                        onClick={handleConfirmForce}
                    >
                        Force link anyway
                    </Button>
                </div>
            </Modal>
        </div>
    );
}
